import { getActionDefinition } from './catalog'
import { warnActivityOnce } from './devWarnings'
import { isValidActivityName, newActivityId } from './headers'
import {
  ACTIVITY_SUBJECT_TYPES,
  ActionDefinition,
  ActivityScope,
  ActivitySubject,
} from './types'

/** A scope ends this long after its last piece of work (handler, request, held confirm). */
export const ACTIVITY_IDLE_MS = 1500

/** A scope never lives longer than this, whatever is still running or held. */
export const ACTIVITY_MAX_LIFETIME_MS = 2 * 60 * 1000

export interface ActivityTrackerOptions {
  idleMs?: number
  maxLifetimeMs?: number
  /** The catalog lookup (defaults to the app's catalog). */
  resolveAction?: (name: string) => ActionDefinition | undefined
  newId?: () => string
  now?: () => number
}

/**
 * A scope kept open while something the action started is waiting for the user (a confirm
 * modal). `resume` runs the continuation (onOk / onCancel) inside the scope again, even when the
 * scope has ended in the meantime; `release` lets the scope go without running anything.
 */
export interface ActivityHold {
  readonly scope: ActivityScope
  resume<T>(fn: () => T): T
  release(): void
}

/** The mutable state of the open scope. A new object for every (re)opening of a scope. */
interface LiveState {
  scope: ActivityScope
  /** Running handlers + requests in flight. */
  pending: number
  /** Unreleased holds (open confirm modals). */
  holds: number
  /** Handlers of the scope running synchronously right now (within() on the call stack). */
  running: number
  /**
   * The user has acted outside the scope's subject (another case tab) since it opened: its
   * requests can no longer be told apart from that tab's, so new ones are not tagged and it can
   * no longer be held.
   */
  detached: boolean
  /**
   * The user has acted (clicked, typed) while the scope was busy and not held: a confirm opened
   * from then on may belong to that new input, so the scope can only be held from its own
   * handler's synchronous run.
   */
  interrupted: boolean
  idleTimer?: ReturnType<typeof setTimeout>
  maxTimer?: ReturnType<typeof setTimeout>
}

const isSameSubject = (a: ActivitySubject, b: ActivitySubject) =>
  a.type === b.type && (a.id ?? null) === (b.id ?? null)

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  !!value && typeof (value as PromiseLike<unknown>).then === 'function'

/**
 * Knows the user action in progress. At most one scope is open at a time: starting an action
 * ends the previous one. A scope ends when its handler has settled, no request it made is still
 * in flight, nothing holds it, and nothing has happened for ACTIVITY_IDLE_MS; or when the next
 * action starts; or when the user acts while it only lingers (noteInteraction); or after
 * ACTIVITY_MAX_LIFETIME_MS. Pure: no React, no axios.
 */
export class ActivityTracker {
  private live: LiveState | null = null
  private readonly requests = new Map<number, LiveState>()
  private nextRequestKey = 1
  private readonly idleMs: number
  private readonly maxLifetimeMs: number
  private readonly resolveAction: (name: string) => ActionDefinition | undefined
  private readonly newId: () => string
  private readonly now: () => number

  constructor(options: ActivityTrackerOptions = {}) {
    this.idleMs = options.idleMs ?? ACTIVITY_IDLE_MS
    this.maxLifetimeMs = options.maxLifetimeMs ?? ACTIVITY_MAX_LIFETIME_MS
    this.resolveAction = options.resolveAction ?? getActionDefinition
    this.newId = options.newId ?? newActivityId
    this.now = options.now ?? (() => Date.now())
  }

  /** The open scope, or null. */
  get current(): ActivityScope | null {
    return this.live?.scope ?? null
  }

  /**
   * Starts the action `name` on `subject`, ending the open scope. Returns the new scope, or null
   * when the action is not tracked (no subject, unknown or disabled name): the previous scope is
   * ended all the same, so the requests of this click are not credited to the previous one.
   */
  begin(
    name: string,
    subject: ActivitySubject | null | undefined
  ): ActivityScope | null {
    this.end()
    if (!this.isTracked(name, subject)) {
      return null
    }
    const scope: ActivityScope = Object.freeze({
      id: this.newId(),
      name,
      subject: Object.freeze({ type: subject.type, id: subject.id ?? null }),
      startedAt: this.now(),
    })
    this.open(scope)
    return scope
  }

  /**
   * Starts the action and runs its handler inside it. A handler that returns a promise keeps the
   * scope open until the promise settles. Returns what the handler returns (untouched).
   */
  run<T>(
    name: string,
    subject: ActivitySubject | null | undefined,
    fn: () => T
  ): T {
    this.begin(name, subject)
    return this.within(this.live, fn)
  }

  /** Ends the open scope (only if it is `scopeId`, when given). */
  end(scopeId?: string): void {
    const state = this.live
    if (!state || (scopeId !== undefined && state.scope.id !== scopeId)) {
      return
    }
    this.close(state)
  }

  /**
   * Keeps the open scope alive until the returned hold is resumed or released (no idle end; the
   * max lifetime still applies). Null when no scope is open, or when the open scope may not be
   * the one the caller continues: it was detached, or the user has acted since it went busy and
   * the call does not come from the scope's own handler (see noteInteraction).
   */
  hold(): ActivityHold | null {
    const state = this.live
    if (
      !state ||
      state.detached ||
      (state.interrupted && state.running === 0)
    ) {
      return null
    }
    state.holds++
    this.clearIdle(state)
    let released = false
    const releaseOnce = () => {
      if (released) {
        return
      }
      released = true
      if (state === this.live) {
        state.holds--
        this.scheduleIdle(state)
      }
    }
    return {
      scope: state.scope,
      release: releaseOnce,
      resume: <T>(fn: () => T): T => {
        releaseOnce()
        let target = this.live
        if (target?.scope.id !== state.scope.id || target.detached) {
          // Ended meanwhile (superseded or past its lifetime): the continuation still belongs
          // to the action the user started, so the scope opens again under the same id.
          target = this.reopen(state.scope)
        }
        // The idle end the release scheduled is cleared again by within() at once.
        return this.within(target, fn)
      },
    }
  }

  /**
   * A request starts: returns the scope it belongs to (the open one) and the key to finish it
   * with, or null when no scope is open.
   */
  trackRequest(): { scope: ActivityScope; key: number } | null {
    const state = this.live
    if (!state || state.detached) {
      return null
    }
    return { scope: state.scope, key: this.addRequest(state) }
  }

  /**
   * A request that already carries the headers of `scopeId` is sent again (the retry after a
   * token refresh): counted in that scope while it is still open. Returns its key, or null.
   */
  rejoinRequest(scopeId: string): number | null {
    const state = this.live
    if (!state || state.scope.id !== scopeId) {
      return null
    }
    return this.addRequest(state)
  }

  /**
   * The user has just acted (pointer down, key down) on the page, inside the controls of
   * `subject` (null: outside every ActivitySubjectProvider, e.g. the menu or an app-wide modal).
   * The open scope only ever covers what its own action does, never new user input:
   * - a scope that is only lingering (handler settled, no request in flight, not held) ends;
   * - a busy scope of another subject is detached: the requests of that other tab would be
   *   tagged with it, and the backend would drop their changes as another subject's;
   * - a busy scope that is not held is marked interrupted (a confirm the new input opens must not
   *   hold it). A held scope is left alone: the input is the answer to its confirm.
   */
  noteInteraction(subject?: ActivitySubject | null): void {
    const state = this.live
    if (!state) {
      return
    }
    if (state.pending === 0 && state.holds === 0) {
      this.close(state)
      return
    }
    if (subject && !isSameSubject(subject, state.scope.subject)) {
      state.detached = true
      return
    }
    if (state.holds === 0) {
      state.interrupted = true
    }
  }

  /** Whether the request `key` is still counted as in flight. */
  isRequestOpen(key: number | null | undefined): boolean {
    return key !== null && key !== undefined && this.requests.has(key)
  }

  /** A tracked request has settled (idempotent). */
  finishRequest(key: number | null | undefined): void {
    if (key === null || key === undefined) {
      return
    }
    const state = this.requests.get(key)
    if (!state) {
      return
    }
    this.requests.delete(key)
    if (state === this.live) {
      state.pending--
      this.scheduleIdle(state)
    }
  }

  private isTracked(
    name: string,
    subject: ActivitySubject | null | undefined
  ): subject is ActivitySubject {
    if (!subject) {
      // No ActivitySubjectProvider above the control: tracking is off there, not a mistake.
      return false
    }
    if (!ACTIVITY_SUBJECT_TYPES.includes(subject.type)) {
      warnActivityOnce(
        `subject:${subject.type}`,
        `subject type "${subject.type}" is not tracked; no activity headers are sent.`
      )
      return false
    }
    const definition = this.resolveAction(name)
    if (!definition) {
      warnActivityOnce(
        `unknown:${name}`,
        `action "${name}" is not in the catalog (src/activity/catalog); no activity headers are sent.`
      )
      return false
    }
    if (definition.enabled === false) {
      warnActivityOnce(
        `disabled:${name}`,
        `action "${name}" is disabled in the catalog; no activity headers are sent.`
      )
      return false
    }
    if (!isValidActivityName(name)) {
      warnActivityOnce(
        `invalid:${name}`,
        `action name "${name}" does not match the backend's naming rule; no activity headers are sent.`
      )
      return false
    }
    return true
  }

  private open(scope: ActivityScope): LiveState {
    const state: LiveState = {
      scope,
      pending: 0,
      holds: 0,
      running: 0,
      detached: false,
      interrupted: false,
    }
    this.live = state
    state.maxTimer = setTimeout(() => {
      if (this.live === state) {
        this.close(state)
      }
    }, this.maxLifetimeMs)
    this.scheduleIdle(state)
    return state
  }

  /** Opens `scope` again (a held continuation of an ended scope): same id, a new lifetime. */
  private reopen(scope: ActivityScope): LiveState {
    this.end()
    return this.open(scope)
  }

  private close(state: LiveState) {
    this.clearIdle(state)
    if (state.maxTimer !== undefined) {
      clearTimeout(state.maxTimer)
      state.maxTimer = undefined
    }
    this.requests.forEach((owner, key) => {
      if (owner === state) {
        this.requests.delete(key)
      }
    })
    if (this.live === state) {
      this.live = null
    }
  }

  private within<T>(state: LiveState | null, fn: () => T): T {
    if (!state) {
      return fn()
    }
    state.pending++
    this.clearIdle(state)
    let settledSync = true
    const done = () => {
      if (state === this.live) {
        state.pending--
        this.scheduleIdle(state)
      }
    }
    state.running++
    try {
      const result = fn()
      if (isThenable(result)) {
        settledSync = false
        result.then(done, done)
      }
      return result
    } finally {
      state.running--
      if (settledSync) {
        done()
      }
    }
  }

  private addRequest(state: LiveState): number {
    const key = this.nextRequestKey++
    this.requests.set(key, state)
    state.pending++
    this.clearIdle(state)
    return key
  }

  private scheduleIdle(state: LiveState) {
    if (state !== this.live) {
      return
    }
    this.clearIdle(state)
    if (state.pending > 0 || state.holds > 0) {
      return
    }
    state.idleTimer = setTimeout(() => {
      if (this.live === state && state.pending === 0 && state.holds === 0) {
        this.close(state)
      }
    }, this.idleMs)
  }

  private clearIdle(state: LiveState) {
    if (state.idleTimer !== undefined) {
      clearTimeout(state.idleTimer)
      state.idleTimer = undefined
    }
  }
}

/** The app's tracker (the axios interceptor and the React hooks share it). */
export const activityTracker = new ActivityTracker()
